import React, { FC } from "react";
import { ITable } from "./Table";
import classNames from "classnames";

export const TableHeader: FC<ITable> = ({
  columns,
  rowSelection,
  data,
  setData,
  expandable,
}) => {
  const [key, setKey] = React.useState("");
  const [type, setType] = React.useState("asc");

  const handleCheck = () => {
    if (data?.length > 0)
      return data?.every((current: { select: boolean }) => current.select);
    else return false;
  };

  const handleAllCheck = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    const newData = [...data];
    newData.forEach((current) => (current.select = checked));
  };

  const onSort = (column) => {
    const compareFunction = (a, b) =>
      a[column]?.toLowerCase().localeCompare(b[column]?.toLowerCase());
    if (type === "asc") {
      data.sort(compareFunction);
    } else if (type === "desc") {
      data.sort((a, b) => compareFunction(b, a));
    }
    setData(data);
  };

  /** Pages pass colours like "bg-[#F36524] text-white"; keep only the bg token. */
  const groupBar = (col: any): string | null => {
    if (col?.key === "FULL_NAME") return "bg-gray-400";
    if (col?.key === "UID") return "bg-sky-400";
    if (!col?.color) return null;
    const bg = String(col.color)
      .split(/\s+/)
      .find((t) => t.startsWith("bg-"));
    return bg ?? null;
  };

  const headCell =
    "px-4 py-2.5 text-left text-[11px] font-semibold uppercase tracking-wider whitespace-nowrap";

  return (
    <thead className="sticky top-0 z-20">
      <tr className="bg-surface border-b border-line">
        {rowSelection && (
          <th className="text-left pl-4 w-10 bg-surface">
            <input
              type="checkbox"
              className="w-3.5 h-3.5 rounded border-line-strong text-primary focus:ring-primary/20 cursor-pointer"
              onChange={handleAllCheck}
              checked={handleCheck()}
            />
          </th>
        )}

        {expandable && <th className="w-10 p-2 bg-surface" />}

        {/* Row number column */}
        <th className={classNames(headCell, "sticky left-0 bg-surface text-ink-muted w-12")}>
          #
        </th>

        {columns?.map(
          (col, i) =>
            (col?.show === undefined || col?.show) && (
              <th
                key={i}
                scope="col"
                className={classNames(headCell, "relative bg-surface text-ink-muted", {
                  sticky: col.freeze,
                  [col.freezeIndex]: col.freeze,
                })}
              >
                {/* Column-group colour: a thin bar instead of a solid fill */}
                {groupBar(col) && (
                  <span
                    aria-hidden="true"
                    className={classNames(
                      "absolute inset-x-0 top-0 h-[3px]",
                      groupBar(col)
                    )}
                  />
                )}
                <span className="inline-flex items-center gap-1">
                  {col.title}
                  {col.hasOwnProperty("header") ? col.header(col) : null}
                  {col.isSort && (
                    <button
                      onClick={() => {
                        setType((prev) => (prev === "asc" ? "desc" : "asc"));
                        onSort(col.key);
                        setKey(col.key);
                      }}
                      className={classNames(
                        "ml-0.5 transition-colors",
                        key === col.key ? "text-primary" : "text-ink-muted hover:text-ink"
                      )}
                    >
                      {key === col.key && type === "desc" ? (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-3 h-3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m19.5 8.25-7.5 7.5-7.5-7.5"
                          />
                        </svg>
                      ) : (
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                          strokeWidth={2}
                          stroke="currentColor"
                          className="w-3 h-3"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="m4.5 15.75 7.5-7.5 7.5 7.5"
                          />
                        </svg>
                      )}
                    </button>
                  )}
                </span>
              </th>
            )
        )}
      </tr>
    </thead>
  );
};
